<script setup lang="ts">
// eslint-disable-next-line
// @ts-ignore
import { Toast } from 'bootstrap/dist/js/bootstrap.bundle.min.js'
import useToasts from '@/composables/useToasts'
import { computed, nextTick, watch, ref } from 'vue'
import { useDevice } from '@/composables/useDevice'
import { onClickOutside } from '@vueuse/core'

const { toasts, clearToast, clearAllNotifications } = useToasts()
const { isMobile } = useDevice()
const notificationsRef = ref(null)
onClickOutside(notificationsRef, () => clearAllNotifications())

watch(toasts, () => nextTick(initializeToasts))
const toastsFiltered = computed(() => toasts.value.filter((toast) => toast.variant !== 'notification'))
const notifications = computed(() => toasts.value.filter((toast) => toast.variant === 'notification'))

function initializeToasts() {
  const toastElList = [].slice.call(document.querySelectorAll('.toast:not(.show):not(.hide)'))
  toastElList.forEach((toastEl: HTMLElement) => {
    const t = Toast.getOrCreateInstance(toastEl)
    t.show()
    toastEl.addEventListener('hidden.bs.toast', removeToast)
  })
}

function removeToast(e: Event) {
  clearToast((e.target as HTMLElement).id)
}
</script>

<template>
  <div
    v-if="toastsFiltered.length > 0"
    class="position-fixed bottom-0 p-3"
    :class="{ 'start-0': !isMobile, 'w-100': isMobile }"
    style="z-index: 9999"
  >
    <div
      v-for="toast in toastsFiltered"
      :id="toast.id"
      :key="toast.id"
      class="toast text-white mb-2"
      :class="[`bg-${toast.variant}`, isMobile ? 'w-100' : '']"
      :data-bs-delay="toast.timeout"
      :data-bs-autohide="toast.autohide"
    >
      <div class="d-flex" :class="toast.variant === 'warning' ? 'text-dark' : 'text-white'">
        <div class="toast-body fs-6">
          <div v-if="toast.text">{{ toast.text }}</div>
          <div v-else-if="toast.customHTML" v-html="toast.customHTML"></div>
          <component :is="toast.customVNode" v-else-if="toast.customVNode"></component>
          <div v-else>Toast</div>
        </div>
        <button
          type="button"
          class="btn d-flex align-items-center me-2 m-auto p-0"
          data-bs-dismiss="toast"
          aria-label="Close"
        >
          <i-bi-x-lg :class="toast.variant === 'warning' ? 'text-dark' : 'text-white'" />
        </button>
      </div>
    </div>
  </div>
  <div
    v-if="notifications.length > 0"
    ref="notificationsRef"
    class="position-fixed p-3 top-0 end-0 mt-6 me-3 w-50"
    :class="{ 'w-100': isMobile }"
    style="z-index: 9999"
  >
    <div
      v-for="notification in notifications"
      :id="notification.id"
      :key="notification.id"
      class="toast w-100 bg-transparent d-flex justify-content-end shadow-none border-0"
      :class="[isMobile ? 'w-100' : '']"
      :data-bs-delay="notification.timeout"
      :data-bs-autohide="notification.autohide"
      data-cy="notification"
    >
      <div
        class="d-flex justify-content-between text-white mb-2 shadow border border-success bg-white rounded-05"
        style="width: 33rem"
      >
        <div class="toast-body fs-6 w-100">
          <div v-if="notification.text">{{ notification.text }}</div>
          <component :is="notification.customVNode" v-if="notification.customVNode"></component>
        </div>
        <button
          type="button"
          class="btn d-flex align-self-baseline me-2 p-0 mt-3"
          data-bs-dismiss="toast"
          aria-label="Close"
          data-cy="notification-close-button"
        >
          <i-bi-x-lg class="text-dark" />
        </button>
      </div>
    </div>
  </div>
</template>
